import React, { useRef, useState, useEffect, useContext } from "react";
import { unstable_batchedUpdates } from "react-dom";
import Swal2 from "sweetalert2";
import "./dropdown.css";
import ProjectContext from "../../context/ProjectContext";
import { convertSlides, CONVERT_KEY, RATIOS, SIZE_NAMES } from "../../Utils/convertCanvas";

/*
 * The video size of the project (standard 16:9, square 1:1, mobile 9:16). Choosing another size
 * converts every scene to it (Utils/convertCanvas): the layers keep their size and shape and are
 * rearranged for the new size, instead of being stretched. A template opened from the Templates
 * page in another size (sessionStorage CONVERT_KEY) is converted here once it has loaded.
 */
const OPTIONS = [
  { size: "16:9", icon: "movie" },
  { size: "1:1", icon: "square" },
  { size: "9:16", icon: "smartphone" },
];

const CustomDropDown = () => {
  const { CurrentProject, setCurrentProject, setchanginCanvasSize, slides, setslides, LayersData, setLayersData } =
    useContext(ProjectContext);
  const dropdownRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const onClick = () => setIsActive(!isActive);

  const [originalCanvasSize, setoriginalCanvasSize] = useState(CurrentProject.canvasSize);
  const [canvasSizeSelect, setcanvasSizeSelect] = useState();

  // Latest scenes and layers, for a conversion started after a delay.
  const latest = useRef({});
  latest.current = { slides, LayersData, canvasSize: CurrentProject.canvasSize };

  const formatCanvasSize = () => {
    const size = RATIOS[originalCanvasSize] ? originalCanvasSize : "9:16";
    sessionStorage.setItem("ProjectAspectRatio", size);
    setcanvasSizeSelect(SIZE_NAMES[size]);
    setchanginCanvasSize(true);
    setCurrentProject({
      ...CurrentProject,
      canvasSize: size,
    });
  };

  useEffect(() => {
    formatCanvasSize();
  }, [originalCanvasSize]);

  // The project's size can change after this menu is shown (a project or template finishing loading).
  useEffect(() => {
    if (CurrentProject.canvasSize && RATIOS[CurrentProject.canvasSize] && CurrentProject.canvasSize !== originalCanvasSize) {
      setoriginalCanvasSize(CurrentProject.canvasSize);
    }
  }, [CurrentProject.canvasSize]);

  /** Converts every scene to `size` and switches the project to it. */
  const convertTo = (size) => {
    const now = latest.current;
    const from = now.canvasSize || originalCanvasSize;
    if (!RATIOS[size] || size === from || !Array.isArray(now.slides)) return;
    // The scene on screen: its layers as they are now (with the last edits).
    const current = now.slides.map((s) =>
      s && s.IsActive && Array.isArray(now.LayersData) && now.LayersData.length ? { ...s, LayerData: now.LayersData } : s
    );
    const converted = convertSlides(current, size, from);
    const active = converted.find((s) => s && s.IsActive);
    // All in one update (this runs after a dialog or a timer, where React does not group updates):
    // the editor copies the layers on screen into the scene whenever the scenes change, so they
    // must already be the converted ones.
    unstable_batchedUpdates(() => {
      if (active && setLayersData) setLayersData(active.LayerData || []);
      setslides(converted);
      setoriginalCanvasSize(size);
    });
  };

  const choose = (size) => {
    setIsActive(false);
    if (size === originalCanvasSize) return;
    Swal2.fire({
      title: "Convert to " + SIZE_NAMES[size] + "?",
      text: "Every scene is rearranged for the new size; nothing is stretched. Check your scenes afterwards. You can convert back at any time.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Convert",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#296AFF",
    }).then((r) => {
      if (r.isConfirmed) convertTo(size);
    });
  };

  // A template opened in another size from the Templates page: converted once its scenes are in.
  useEffect(() => {
    let want = null;
    try {
      want = sessionStorage.getItem(CONVERT_KEY);
    } catch (e) {
      want = null;
    }
    if (!want) return undefined;
    if (!RATIOS[want]) {
      sessionStorage.removeItem(CONVERT_KEY);
      return undefined;
    }
    const loaded = Array.isArray(slides) && slides.some((s) => s && Array.isArray(s.LayerData) && s.LayerData.length);
    if (!loaded) return undefined;
    // Waits until the editor has finished placing the scenes on the screen.
    const t = setTimeout(() => {
      sessionStorage.removeItem(CONVERT_KEY);
      convertTo(want);
    }, 1500);
    return () => clearTimeout(t);
  }, [slides]);

  return (
    <div className="container">
      <div className="menu-container">
        <button onClick={onClick} className="menu-trigger" title="Video size: choose another to convert the video">
          <span className="material-icons mr-2" style={{ fontSize: "20px" }}>
            movie
          </span>
          <span>{canvasSizeSelect}</span>
        </button>
        <nav ref={dropdownRef} className={`menu ${isActive ? "active" : "inactive"}`}>
          <div className="menu-head">Video size</div>
          <ul>
            {OPTIONS.map((o) => (
              <li key={o.size} onClick={() => choose(o.size)}>
                <div className="wrapper">
                  <span className="material-icons mr-2">{o.icon}</span>
                  <div>
                    <div className="label">{SIZE_NAMES[o.size]}</div>
                    {originalCanvasSize !== o.size && <div className="sublabel">Convert all scenes</div>}
                  </div>
                </div>
                {originalCanvasSize === o.size && <span className="material-icons checkIcon mr-2">check</span>}
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
};
export default CustomDropDown;
